import { analyzeKeyword } from './keywordAnalyzer';
import { analyzeTitle } from './titleAnalyzer';
import { analyzeMeta } from './metaAnalyzer';
import { analyzeContent } from './contentAnalyzer';
import { analyzeReadability } from './readabilityAnalyzer';
import { analyzeTechnical } from './technicalAnalyzer';

/**
 * Main Orchestrator for SEO Analysis
 * Aggregates scores from all individual analyzers to return a final 0-100 score.
 */

export const calculateSeoScore = (blogData) => {
  if (!blogData || Object.keys(blogData).length === 0) {
    return {
      score: 0,
      breakdown: {},
      suggestions: [{ status: "error", text: "No blog data provided." }]
    };
  }

  // Run all analyzers
  const keywordAnalysis = analyzeKeyword(blogData);
  const titleAnalysis = analyzeTitle(blogData);
  const metaAnalysis = analyzeMeta(blogData);
  const contentAnalysis = analyzeContent(blogData);
  const readabilityAnalysis = analyzeReadability(blogData);
  const technicalAnalysis = analyzeTechnical(blogData);

  // Calculate totals
  const totalScoreCalc = 
    keywordAnalysis.score +
    titleAnalysis.score +
    metaAnalysis.score +
    contentAnalysis.score +
    readabilityAnalysis.score +
    technicalAnalysis.score;
  
  const totalMaxScore = 
    keywordAnalysis.maxScore + 
    titleAnalysis.maxScore + 
    metaAnalysis.maxScore + 
    contentAnalysis.maxScore + 
    readabilityAnalysis.maxScore + 
    technicalAnalysis.maxScore;

  // Normalize to 100
  let normalizedScore = Math.round((totalScoreCalc / totalMaxScore) * 100);
  if (normalizedScore > 100) normalizedScore = 100;
  if (normalizedScore < 0) normalizedScore = 0;

  // Grouped suggestions for the checklist component
  const breakdown = {
    keyword: keywordAnalysis.results,
    title: titleAnalysis.results,
    meta: metaAnalysis.results,
    content: contentAnalysis.results,
    readability: readabilityAnalysis.results,
    technical: technicalAnalysis.results
  };

  // Flat list of suggestions (backward compatible with old api but mostly use breakdown now)
  const suggestions = [
    ...keywordAnalysis.results,
    ...titleAnalysis.results,
    ...metaAnalysis.results,
    ...contentAnalysis.results,
    ...readabilityAnalysis.results,
    ...technicalAnalysis.results
  ];

  return {
    score: normalizedScore,
    breakdown,
    suggestions
  };
};

export const getSeoColor = (score) => {
  if (score < 40) return "#ef4444"; // Red / Poor
  if (score <= 90) return "#fbbf24"; // Yellow / Needs Improvement
  return "#10b981"; // Green / Excellent (above 90)
};

/**
 * Returns a human-readable label for the score.
 */
export const getSeoLabel = (score) => {
  if (score < 40) return "Poor";
  if (score < 70) return "Needs Improvement";
  return "Good";
};
